import React, { useState, useRef, useEffect } from "react";
import DynamicForm, { FieldConfig } from "./DynamicForm";
import { useTheme } from "../../context/ThemeContext";
import { useNotification } from "../../context/NotificationContext";
import { fetchData } from "../../services/FetchDataService";
import { toSnakeCase } from "../utils/toSnakeCase";

export interface Step {
  title?: string;
  fields: FieldConfig[];
  initialValues?: { [key: string]: any };
  additionalValues?: { [key: string]: any };
  icon?: string;
  endpoint_create: string;
  endpoint_get: string;
  endpoint_update?: string;
  statusKey?: string;
  action?: string;
  sendAsArray?: boolean;
}

export interface StepStatus {
  current_step: string;
  next_step: string;
  [key: string]: string | boolean;
}

export interface StepperFormProps {
  steps: Step[];
  onSubmit: (data: { [key: string]: any }) => void;
  onCancel?: () => void;
  client?: any;
  stepStatus?: StepStatus;
  filterID?: number;
  output?: (response: any, step: Step, stepIndex: number) => void;
}

const StepperForm: React.FC<StepperFormProps> = ({
  steps,
  onSubmit,
  onCancel,
  client,
  stepStatus,
  filterID,
  output,
}) => {
  const { theme } = useTheme();
  const { success, error } = useNotification();
  let actionType: string;

  const [currentStep, setCurrentStep] = useState(0);
  const [aggregatedData, setAggregatedData] = useState<{ [key: string]: any }>(
    {}
  );
  const [data, setData] = useState<{ [key: string]: any }>({});
  const formRef = useRef<{ submitForm: () => void }>(null);

  // Local array tracks which steps have been completed
  const [completedSteps, setCompletedSteps] = useState<boolean[]>(() =>
    steps.map(() => false)
  );

  const currentStepConfig = steps[currentStep];

  // Log which step we’re displaying:
  console.log(
    "Rendering step index:",
    currentStep,
    "=>",
    currentStepConfig.title
  );

  const defaultValues = {
    ...currentStepConfig.initialValues,
    ...currentStepConfig.additionalValues,
    ...aggregatedData,
    ...data, // put this last so it "wins" if keys overlap
  };
  // Debug: Log the default values going into DynamicForm
  console.log("defaultValues for current step:", defaultValues);

  useEffect(() => {
    async function fetchStepData() {
      console.log("fetchStepData() called for step:", currentStepConfig.title);
      // Only fetch if initialValues are empty
      if (
        !currentStepConfig.initialValues ||
        Object.keys(currentStepConfig.initialValues).length === 0
      ) {
        console.log(
          "No initialValues. Attempting to fetch data from endpoint:",
          currentStepConfig.endpoint_get
        );
        try {
          const filter = { id: filterID };
          console.log("Using filter:", filter);
          const response = await fetchData(
            client,
            currentStepConfig.endpoint_get,
            filter
          );
          const { code, data } = response;
          console.log("fetchStepData() response:", response);

          if (data && code === 200) {
            console.log(`Data for step "${currentStepConfig.title}":`, data);
            setData(() => ({ ...data }));
          } else {
            console.warn(
              `No valid data returned for step "${currentStepConfig.title}"`
            );
          }
        } catch (err) {
          console.error(
            `Error fetching data for ${currentStepConfig.title}:`,
            err
          );
        }
      } else {
        console.log("Step has initialValues, skipping fetch.");
      }
    }
    fetchStepData();
  }, [currentStep]);

  // Keep the original stepStatus logic if needed
  useEffect(() => {
    if (stepStatus) {
      const currentStepIndex = steps.findIndex(
        (step) =>
          step.statusKey &&
          stepStatus.current_step &&
          step.statusKey.toLowerCase() === stepStatus.current_step.toLowerCase()
      );
      if (currentStepIndex >= 0 && currentStepIndex !== currentStep) {
        console.log(
          "StepStatus changed, moving to step index:",
          currentStepIndex
        );
        setCurrentStep(currentStepIndex);
      }
    }
  }, [stepStatus, steps]);

  const handleStepSubmit = async (stepData: { [key: string]: any }) => {
    console.log(
      `handleStepSubmit() stepData for step "${currentStepConfig.title}":`,
      stepData
    );
    const updatedData = {
      ...aggregatedData,
      ...stepData,
      ...currentStepConfig.additionalValues,
      action: actionType,
    };
    console.log(`Updating aggregatedData with:`, updatedData);
    setAggregatedData(updatedData);

    const isExisting =
      currentStepConfig.initialValues &&
      Object.keys(currentStepConfig.initialValues).length > 0;
    const endpoint = isExisting
      ? currentStepConfig.endpoint_get
      : currentStepConfig.endpoint_create;

    console.log(
      "Submitting data to endpoint:",
      endpoint,
      "with payload:",
      updatedData
    );

    try {
      const response = await client.post(endpoint, updatedData);
      console.log("Response from server:", response);

      if (response.data) {
        if (typeof output === "function") {
          output(response.data, currentStepConfig, currentStep);
        }
        setAggregatedData((prev) => ({ ...prev, ...response.data }));
      }
      if (response.code === 200) {
        console.log(`Step "${currentStepConfig.title}" saved successfully.`);
        setCompletedSteps((prev) => {
          const newArr = [...prev];
          newArr[currentStep] = true;
          return newArr;
        });
        success(`${currentStepConfig.title} saved successfully`);
        setAggregatedData((prev) => ({ ...prev, ...updatedData }));
      }
    } catch (err) {
      console.error("Error saving step data:", err);
      error(`Failed to save ${currentStepConfig.title}`);
      return;
    }

    if (currentStep === steps.length - 1) {
      console.log(
        "Final step reached. onSubmit called with data:",
        updatedData
      );
      onSubmit(updatedData);
    } else {
      console.log("Moving to next step:", currentStep + 1);
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSave = () => {
    actionType = "Save";
    console.log("handleSave() => calling submitForm()");
    formRef.current?.submitForm();
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      console.log("handlePrev() => moving to previous step:", currentStep - 1);
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveAndContinue = () => {
    actionType = "Submit";
    console.log("handleSaveAndContinue() => calling submitForm()");
    formRef.current?.submitForm();
  };

  const handleStepClick = (index: number) => {
    console.log("handleStepClick() => user clicked step index:", index);
    setCurrentStep(index);
  };

  function isStepCompleted(
    stepStatus: StepStatus | undefined,
    statusKey: string | undefined
  ): boolean {
    if (statusKey) {
      const normalizedKey = toSnakeCase(statusKey);
      if (stepStatus && stepStatus[normalizedKey]) {
        return true;
      }
    }
    return false;
  }

  return (
    <div className="w-full px-8 py-4">
      {/* Step Indicators */}
      <div className="relative flex items-center justify-between w-full">
        <div className="absolute left-0 top-2/4 h-0.5 w-full -translate-y-2/4 bg-primary-200"></div>
        <div
          className="absolute left-0 top-2/4 h-0.5 -translate-y-2/4 bg-primary-600 transition-all duration-500"
          style={{
            width:
              steps.length > 1
                ? `${(currentStep / (steps.length - 1)) * 100}%`
                : "100%",
          }}
        ></div>
        {steps.map((step, index) => {
          const isCompletedLocal = completedSteps[index];
          const isCompletedExternal = isStepCompleted(
            stepStatus,
            step.statusKey
          );
          const isCompleted = isCompletedLocal || isCompletedExternal;

          const isCurrent =
            step.statusKey && stepStatus
              ? stepStatus.current_step.toLowerCase() ===
                step.statusKey.toLowerCase()
              : index === currentStep;

          let stepClasses =
            "relative z-10 grid w-10 h-10 font-bold transition-all duration-300 rounded-full place-items-center cursor-pointer";

          if (isCompleted) {
            stepClasses += " bg-tertiary-A700";
          } else if (index <= currentStep) {
            stepClasses += " bg-primary-600";
          } else {
            stepClasses += " bg-primary-200";
          }

          if (isCurrent) {
            stepClasses += " border-2 border-current";
          }

          return (
            <div
              key={index}
              onClick={() => handleStepClick(index)}
              // 1) Position each step icon "in the middle" of the line
              className="flex flex-col items-center relative"
              style={{ transform: "translateY(10%) " }}
            >
              {/* The step circle (icon or number) */}
              <div className={stepClasses}>
                {step.icon ? (
                  <i
                    className={`material-icons ${
                      theme === "night"
                        ? "text-gray-50 hover:text-primary-700"
                        : "text-gray-50 hover:text-primary-500"
                    }`}
                    style={{ fontSize: "20px" }}
                  >
                    {step.icon}
                  </i>
                ) : (
                  index + 1
                )}
              </div>

              {/* Step title below the circle */}
              <div
                className={`text-xs mt-1 text-center ${
                  theme === "night" ? "text-primary-700" : "text-primary-800"
                }`}
              >
                {/* You can truncate if titles are long or style as you wish */}
                {step.title}
              </div>
            </div>
          );
        })}
      </div>
      {/* Form */}
      <div className="mt-8">
        <DynamicForm
          ref={formRef}
          title={currentStepConfig.title || `Step ${currentStep + 1}`}
          fields={currentStepConfig.fields}
          defaultValues={defaultValues}
          onSubmit={handleStepSubmit}
          submitText="Save and Exit"
          showCancel={false}
        />
      </div>

      {/* Buttons */}
      <div className="flex justify-between mt-16">
        <button
          type="button"
          className="select-none rounded-lg bg-primary-600 py-3 px-6 text-center font-sans text-xs font-bold uppercase text-white shadow-md shadow-primary-600/10 transition-all hover:shadow-lg hover:shadow-primary-600/20 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          onClick={currentStep === 0 && onCancel ? onCancel : handlePrev}
        >
          {currentStep === 0 ? "Cancel" : "Prev"}
        </button>
        <button
          type="button"
          className="select-none rounded-lg bg-primary-600 py-3 px-6 text-center font-sans text-xs font-bold uppercase text-white shadow-md shadow-primary-600/10 transition-all hover:shadow-lg hover:shadow-primary-600/20 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          onClick={handleSaveAndContinue}
        >
          {currentStep === steps.length - 1 ? "Submit" : "Save and Continue"}
        </button>
      </div>
    </div>
  );
};

export default StepperForm;
